import React, { useEffect, useRef, useState } from "react";

function loadScript(src, onLoad, onError) {
  const existing = document.querySelector(
    `script[src^="${src.split("?")[0]}"]`,
  );
  if (existing) {
    onLoad && onLoad();
    return existing;
  }
  const script = document.createElement("script");
  script.src = src;
  script.async = true;
  script.defer = true;
  if (onLoad) script.onload = onLoad;
  if (onError) script.onerror = onError;
  document.head.appendChild(script);
  return script;
}

const CaptchaWidget = ({
  provider,
  siteKey,
  fallbackSiteKey,
  onVerify,
  onExpire,
}) => {
  const containerRef = useRef(null);
  const widgetIdRef = useRef(null);
  const [activeProvider, setActiveProvider] = useState(provider);
  const [activeSiteKey, setActiveSiteKey] = useState(siteKey);
  const initializedRef = useRef(false);

  const mountTurnstile = (key) => {
    const doRender = () => {
      if (window.turnstile && containerRef.current) {
        widgetIdRef.current = window.turnstile.render(containerRef.current, {
          sitekey: key,
          callback: (token) => onVerify(token, "turnstile"),
          "expired-callback": () => {
            onExpire && onExpire();
          },
        });
      }
    };

    loadScript(
      "https://challenges.cloudflare.com/turnstile/v0/api.js",
      doRender,
      () => {
        if (fallbackSiteKey) {
          initializedRef.current = false;
          setActiveProvider("recaptcha");
          setActiveSiteKey(fallbackSiteKey);
        }
      },
    );
  };

  const mountRecaptcha = (key) => {
    const cbName = "_grecaptchaOnLoad";
    const doRender = () => {
      if (window.grecaptcha && containerRef.current) {
        widgetIdRef.current = window.grecaptcha.render(containerRef.current, {
          sitekey: key,
          callback: (token) => onVerify(token, "recaptcha"),
          "expired-callback": () => {
            onExpire && onExpire();
          },
        });
      }
    };
    window[cbName] = doRender;

    /* Google calls the onload callback once, when the script first loads. On any
     * later mount -- navigating between landing pages, or the form remounting --
     * loadScript finds the existing tag and returns, and the recaptcha branch
     * passes no onLoad, so nothing rendered the widget and the submit button
     * stayed disabled for good. Render directly when the API is already there. */
    if (window.grecaptcha && window.grecaptcha.render) {
      doRender();
      return;
    }

    loadScript(
      `https://www.google.com/recaptcha/api.js?onload=${cbName}&render=explicit`,
      null,
      null,
    );
  };

  // declared after the two mount helpers so neither is referenced before it is
  // defined; the effect still runs after the first render, as before
  useEffect(() => {
    if (!activeSiteKey || initializedRef.current) return;
    initializedRef.current = true;

    if (activeProvider === "turnstile") {
      mountTurnstile(activeSiteKey);
    } else {
      mountRecaptcha(activeSiteKey);
    }

    return () => {
      if (
        activeProvider === "turnstile" &&
        widgetIdRef.current !== null &&
        window.turnstile
      ) {
        try {
          window.turnstile.remove(widgetIdRef.current);
        } catch (_) {}
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeProvider, activeSiteKey]);

  return <div ref={containerRef} style={{ marginTop: "8px" }} />;
};

export default CaptchaWidget;
